// /auth/callback?th=<token_hash> — server-side verification of the emailed
// link (POST /api/ctr/auth/verify), then the session cookie and onward
// routing: school host → its dashboard; apex with one school → that school's
// dashboard; several → the chooser; none → signup pointer. A failed verify
// lands on /signin?expired=1.
import { useEffect, useState } from 'react';
import { api, slugFromLocation } from '@/lib/api.js';
import { setSessionToken, dashboardUrlFor } from '@/lib/auth.js';
import Wordmark from '@/components/Wordmark.jsx';
import Spinner from '@/components/ui/Spinner.jsx';

export default function AuthCallback() {
  const [state, setState] = useState('verifying'); // verifying | choose | none
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    const th = new URLSearchParams(window.location.search).get('th');
    if (!th) { window.location.replace('/signin?expired=1'); return; }
    api.post('/api/ctr/auth/verify', { token_hash: th })
      .then((r) => {
        setSessionToken(r.token);
        const hostSlug = slugFromLocation();
        const mine = r.schools || [];
        if (hostSlug && mine.some((s) => s.slug === hostSlug)) { window.location.replace('/dashboard'); return; }
        if (mine.length === 1) { window.location.replace(dashboardUrlFor(mine[0].slug)); return; }
        if (mine.length > 1) { setSchools(mine); setState('choose'); return; }
        setState('none');
      })
      .catch(() => { window.location.replace('/signin?expired=1'); });
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-paper px-5">
      <Wordmark className="mb-6 text-xl text-primary" />
      {state === 'verifying' ? (
        <div className="flex items-center gap-2.5 text-sm text-ink-dim"><Spinner /> Signing you in…</div>
      ) : null}
      {state === 'choose' ? (
        <div className="w-full max-w-[420px] rounded-panel border border-line bg-white p-[26px_28px] shadow-card">
          <h1 className="mb-1.5 text-[19px] font-bold tracking-[-0.01em]">Pick a dashboard</h1>
          <p className="mb-4 text-[13px] leading-[1.55] text-ink-dim">You&apos;re on more than one school&apos;s team. You can switch any time from the sidebar.</p>
          <div className="flex flex-col gap-2">
            {schools.map((s) => (
              <a key={s.slug} href={dashboardUrlFor(s.slug)}
                className="flex min-h-[48px] items-center justify-between rounded-card-lg border-[1.5px] border-line bg-white px-4 text-[14px] font-semibold text-ink hover:border-primary">
                <span>{s.name}</span>
                <span className="rounded-pill bg-fill px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.04em] text-ink-dim">{s.role}</span>
              </a>
            ))}
          </div>
        </div>
      ) : null}
      {state === 'none' ? (
        <div className="w-full max-w-[420px] rounded-panel border border-line bg-white p-[26px_28px] text-center shadow-card">
          <h1 className="mb-1.5 text-[19px] font-bold tracking-[-0.01em]">You&apos;re signed in, but no dashboard yet</h1>
          <p className="mb-4 text-[13px] leading-[1.6] text-ink-dim">
            This email isn&apos;t on any school&apos;s team. Set up your school&apos;s portal, or ask a teammate to invite you from their Team page.
          </p>
          <a href="https://clearedto.rent/signup" className="inline-block rounded-card bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-deep">
            Set up my portal
          </a>
        </div>
      ) : null}
    </div>
  );
}
